import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { EditInDialogButtonProps } from '@react-admin/ra-form-layout';
import { isAfter } from 'date-fns';
import { omit } from 'lodash';
import { useCallback, useMemo } from 'react';
import { RaRecord, useNotify, useRecordContext, useRefresh } from 'react-admin';

import {
  GetB2bCampaignsIdEndpoint,
  isB2bCampaignEditableBoExternal,
  isOngoingConsultationCampaignEdit,
  isPaymentMethodOnlyEdit,
} from '@Common/types/bo/b2b/campaigns';

import { EditInDialogButton, StepperForm } from '@sh4res/bo-common/components';
import {
  getSteps,
  useCampaignConfirmationDialog,
} from '@sh4res/bo-common/components/B2bCampaigns/common';
import { toUtcDateString } from '@sh4res/bo-common/components/B2bCampaigns/consultationDates';
import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import { handleCampaignTransform } from '@sh4res/bo-common/components/B2bCampaigns/transform';
import { useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { getErrorMessage, Resources } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type CampaignEditProps = EditInDialogButtonProps['ButtonProps'] & {
  resource: Resources;
};

const distributorFlowDraftStatuses: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['status'][] =
  [
    'campaign_draft',
    'campaign_pending_allocation_computation',
    'campaign_awaiting_customer_confirmation',
  ];

export function B2bCampaignsEdit({ resource, ...props }: CampaignEditProps) {
  const record = useRecordContext<GetB2bCampaignsIdEndpoint['Client']['ResponseBody']>();
  const confirmation = useCampaignConfirmationDialog();

  // Opt-in entry point for an internal BO agent to also run the distributor-owned stages.
  const canOverrideDistributorSteps = useMemo(
    (): boolean =>
      !isBoExternal() &&
      !!record &&
      !record.is_cancelled &&
      !!record.is_draft &&
      record.allocation_computed_by === 'distributor' &&
      !isCampaignVacationDaysAllocation(record) &&
      distributorFlowDraftStatuses.includes(record.status),
    [record],
  );

  if (!record) {
    return null;
  }

  return (
    <>
      <CampaignEditDialogButton
        resource={resource}
        confirmation={confirmation}
        record={record}
        testId="BoExternalB2bCampaignsEdit_EditButton"
        {...props}
      />
      {canOverrideDistributorSteps && (
        <CampaignEditDialogButton
          resource={resource}
          confirmation={confirmation}
          record={record}
          testId="BoExternalB2bCampaignsEdit_ResumeButton"
          distributorStepsOverride
          {...props}
        />
      )}
      {confirmation.Dialog}
    </>
  );
}

function CampaignEditDialogButton({
  resource,
  confirmation,
  record,
  testId,
  distributorStepsOverride = false,
  ...props
}: CampaignEditProps & {
  confirmation: ReturnType<typeof useCampaignConfirmationDialog>;
  record: GetB2bCampaignsIdEndpoint['Client']['ResponseBody'];
  testId: string;
  distributorStepsOverride?: boolean;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { upload } = useUploadPresignedUrl();

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.campaigns.actions.edit.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(
    (record: RaRecord) => {
      confirmation.open({
        campaignId: record.id as number,
        title: 'resources.bo_common.b2b.campaigns.sections.success_modal_updated.title',
        body: 'resources.bo_common.b2b.campaigns.sections.success_modal_updated.body',
        showAllocations: true,
      });
      refresh();
    },
    [refresh, confirmation],
  );

  const handleTransform = useCallback(
    (isDraft: boolean) => handleCampaignTransform({ isDraft, action: 'edit', upload }),
    [upload],
  );

  const isCetCampaign = isCampaignVacationDaysAllocation(record);
  const isStartDateInFuture = !!record.start_date && isAfter(record.start_date, new Date());

  const isOngoingConsultationEdit = useMemo(
    (): boolean =>
      !record.is_cancelled && !isCetCampaign && isOngoingConsultationCampaignEdit(record),
    [record, isCetCampaign],
  );

  const isFundRequestPaymentMethodEdit = useMemo(
    (): boolean =>
      !isBoExternal() &&
      !record.is_cancelled &&
      !record.is_draft &&
      isPaymentMethodOnlyEdit({ ...record, state: record.status }),
    [record],
  );

  /**
   * a published CET campaign only allows its consultation period to be edited, in the internal BO
   * only, unless it is stuck in a failed state where only the payment method can change (the backend
   * then ignores the dates)
   */
  const isCetConsultationPeriodEdit = useMemo(
    (): boolean =>
      !isBoExternal() &&
      isCetCampaign &&
      !isFundRequestPaymentMethodEdit &&
      !record.is_cancelled &&
      !record.is_draft &&
      (isStartDateInFuture || isOngoingConsultationCampaignEdit(record)),
    [record, isCetCampaign, isFundRequestPaymentMethodEdit, isStartDateInFuture],
  );

  const steps = useMemo(
    () =>
      getSteps({
        isBoExternal: isBoExternal(),
        campaignStatus: record.status,
        action: 'edit',
        openConfirmationDialog: confirmation.open,
        isOngoingConsultationEdit,
        distributorStepsOverride,
        isFundRequestPaymentMethodEdit,
        isCetConsultationPeriodEdit,
      }),
    [
      record.status,
      confirmation,
      isOngoingConsultationEdit,
      distributorStepsOverride,
      isFundRequestPaymentMethodEdit,
      isCetConsultationPeriodEdit,
    ],
  );

  const isCETAmountAllocation = useMemo(
    () =>
      isCampaignVacationDaysAllocation(record) && record.status === 'campaign_ending_grace_period',
    [record],
  );

  const disabled = useMemo((): boolean => {
    if (record.is_cancelled) {
      return true;
    }

    // allow allocate money amounts
    if (isCETAmountAllocation) {
      return false;
    }

    if (isBoExternal() && !isB2bCampaignEditableBoExternal(record.status)) {
      return true;
    }

    if (record.is_draft) {
      if (isBoExternal() && record.status === 'campaign_pending_allocation_computation') {
        return true;
      }
      if (
        !isBoExternal() &&
        !distributorStepsOverride &&
        record.status === 'campaign_awaiting_customer_confirmation'
      ) {
        return true;
      }
      // if not in those 2 states from the allocation computation flow, always allowed
      return false;
    }
    // allow editing when campaign campaign choices are collected via app and campaign has not yet started
    if (record.choice_collected_by === 'distributor' && isStartDateInFuture && !isCetCampaign) {
      return false;
    }

    // allow editing a restricted set of fields while the consultation is ongoing
    if (isOngoingConsultationEdit || isCetConsultationPeriodEdit) {
      return false;
    }

    /**
     * allow changing the payment method (and only that) once the fund request is created, or while
     * stuck in a failed state before it
     */
    if (isFundRequestPaymentMethodEdit) {
      return false;
    }

    // disable otherwise
    return true;
  }, [
    record,
    isCetCampaign,
    isStartDateInFuture,
    isCETAmountAllocation,
    isOngoingConsultationEdit,
    isCetConsultationPeriodEdit,
    isFundRequestPaymentMethodEdit,
    distributorStepsOverride,
  ]);

  const defaultValues = useMemo(() => getCampaignEditFormDefaults(record), [record]);

  return (
    <EditInDialogButton
      resource={resource}
      fullWidth
      maxWidth="xl"
      title={
        distributorStepsOverride
          ? 'resources.bo_common.b2b.campaigns.actions.resume.dialog_title'
          : 'resources.bo_common.b2b.campaigns.actions.edit.label'
      }
      {...(distributorStepsOverride && {
        label: 'resources.bo_common.b2b.campaigns.actions.resume.label',
        icon: <PlayArrowIcon />,
      })}
      testId={testId}
      ButtonProps={{
        variant: 'contained',
        color: distributorStepsOverride ? 'warning' : 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
        },
        ...props,
        disabled,
      }}
      transform={handleTransform(false)}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess, onError }}
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          height: '100%',
        },
        '& form': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}>
      <StepperForm
        defaultValues={defaultValues}
        alwaysEnableSaveButton
        resetOptions={{ keepValues: true }}
        draft={
          record.is_draft || isCETAmountAllocation
            ? {
                transform: handleTransform(!isCETAmountAllocation),
                resource,
                action: 'update',
                resourceId: record.id,
              }
            : undefined
        }
        steps={steps}
        sx={{
          flexGrow: 1,
          '& > div': {
            height: '100%',
          },
        }}
        permissions={{
          resource,
          action: 'edit',
        }}
        testId="BoExternalB2bCampaignsEdit"
      />
    </EditInDialogButton>
  );
}

function populateLateInterestFormFields(
  record: GetB2bCampaignsIdEndpoint['Client']['ResponseBody'],
): {
  is_automatic_late_payment_interests: 'yes' | 'no' | undefined;
  late_payment_interests_override_amount: number | undefined;
} {
  if (record.is_late_payment_interests) {
    if (record.late_payment_interests_override_amount === null) {
      return {
        is_automatic_late_payment_interests: 'yes',
        late_payment_interests_override_amount: undefined,
      };
    } else {
      return {
        is_automatic_late_payment_interests: 'no',
        late_payment_interests_override_amount: record.late_payment_interests_override_amount,
      };
    }
  } else {
    return {
      // Interests off is a known answer, not a missing one: leaving it undefined shows the radio blank.
      is_automatic_late_payment_interests: 'no',
      late_payment_interests_override_amount: undefined,
    };
  }
}

export function getCampaignEditFormDefaults(
  record: GetB2bCampaignsIdEndpoint['Client']['ResponseBody'],
) {
  return {
    ...omit(record, 'additional_savings_funds_requests'),
    ...populateLateInterestFormFields(record),
    start_date: toUtcDateString(record.start_date),
    end_date: toUtcDateString(record.end_date),
  };
}
